import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Button, LinearProgress } from '@mui/material';

const PREROLL_ZONE = '01DXF6DT004000000000002P2M';
const LINK_ZONE   = '01DXF6DT004000000000002P2G';
const KEYWORDS    = 'live,cam,girls,adult,webcam';
// Cambuilder VAST — primary source (tagged AFNO=1-70001)
const CB_VAST_URL = 'https://as.sexad.net/vast/kwiky?afno=1-70001&niche=allgirls&target=https://instacamgirlslive.com/landing/click/&hn=instacamgirlslive.com';
// Clickthrough URL for the pre-roll ad
const AD_CLICK_URL = 'https://instacamgirlslive.com/landing/click/';

/**
 * Pre-Roll ad wrapper.
 * Shows a VAST video ad for up to 5s (skippable after 5s),
 * then calls onComplete() to show the actual content (cam embed).
 */
export const PreRollAd = ({ onComplete, keywords = KEYWORDS }) => {
  const videoRef   = useRef(null);
  const [vastUrl, setVastUrl]       = useState(null);
  const [clickUrl, setClickUrl]     = useState(AD_CLICK_URL);
  const [countdown, setCountdown]   = useState(5);
  const [skippable, setSkippable]   = useState(false);
  const [adError, setAdError]       = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // Try Cambuilder VAST first, fall back to Twinred
    const tryVast = (url) => fetch(url, { headers: { Referer: 'https://instacamgirls.com/' } })
      .then(r => r.text())
      .then(xml => {
        const parser = new DOMParser();
        const doc    = parser.parseFromString(xml, 'text/xml');
        const media  = doc.querySelector('MediaFile');
        // Also try to extract ClickThrough URL from VAST
        const clickThrough = doc.querySelector('ClickThrough');
        if (clickThrough && clickThrough.textContent.trim()) {
          setClickUrl(clickThrough.textContent.trim());
        }
        if (media) {
          setVastUrl(media.textContent.trim());
          return true;
        }
        return false;
      })
      .catch(() => false);

    tryVast(CB_VAST_URL).then(ok => {
      if (!ok) {
        const twinredUrl = `https://ad.twinrdengine.com/adraw?zone=${PREROLL_ZONE}&kw=${keywords}&t=vast`;
        return tryVast(twinredUrl);
      }
    }).then(ok => {
      if (ok === false) { setAdError(true); onComplete(); }
    });
  }, [keywords, onComplete]);

  useEffect(() => {
    if (!vastUrl) return;
    timerRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { setSkippable(true); clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [vastUrl]);

  if (adError || !vastUrl) return null;

  const handleAdClick = (e) => {
    e.stopPropagation();
    window.open(clickUrl, '_blank', 'noopener');
  };

  return (
    <Box sx={{
      position: 'fixed', inset: 0, bgcolor: '#000', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
    }}>
      {/* Skip button — fixed to top-right of screen, above everything including ad iframes */}
      <Box sx={{
        position: 'fixed', top: 12, right: 12, zIndex: 10002,
        pointerEvents: 'all',
      }}>
        {skippable ? (
          <Button variant="contained" size="small"
            sx={{ bgcolor: 'rgba(0,0,0,0.85)', color: '#fff', fontSize: '0.8rem',
              border: '1px solid rgba(255,255,255,0.3)', borderRadius: 1,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.95)' } }}
            onClick={(e) => { e.stopPropagation(); onComplete(); }}>
            Skip Ad ▶
          </Button>
        ) : (
          <Box sx={{ bgcolor: 'rgba(0,0,0,0.85)', color: '#fff', px: 1.5, py: 0.5,
            borderRadius: 1, fontSize: '0.75rem', border: '1px solid rgba(255,255,255,0.2)' }}>
            Skip in {countdown}s
          </Box>
        )}
      </Box>

      <Box sx={{ position: 'relative', width: { xs: '100vw', md: 800 }, maxWidth: '100vw' }}>
        <video
          ref={videoRef}
          src={vastUrl}
          autoPlay
          muted={false}
          playsInline
          style={{ width: '100%', display: 'block', pointerEvents: 'none' }}
          onEnded={onComplete}
        />
        {/* Transparent clickable overlay — opens ad URL, does NOT pause video */}
        <Box
          onClick={handleAdClick}
          sx={{
            position: 'absolute', inset: 0,
            cursor: 'pointer',
          }}
        />
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
          <LinearProgress
            variant="determinate"
            value={skippable ? 100 : ((5 - countdown) / 5) * 100}
            sx={{ height: 3, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: '#FF4196' } }}
          />
        </Box>
      </Box>
      <Typography variant="caption" sx={{ color: '#666', mt: 1 }}>Advertisement</Typography>
    </Box>
  );
};

/**
 * Link zone ad — opens Twinred link ad in new tab.
 * Call triggerLinkAd() on any click event to register an impression + open ad.
 */
export const triggerLinkAd = (keywords = KEYWORDS) => {
  const url = `https://ad.twinrdengine.com/adraw?zone=${LINK_ZONE}&kw=${keywords}`;
  window.open(url, '_blank', 'noopener');
};

export default PreRollAd;
